// Shared wizard wrapper component for offer editing screens
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Card, Spinner } from 'react-bootstrap';
import { get_datos_oferta_erp, aceptar_oferta, sync_renovacion_progress, relanzar_oferta, cancelar_oferta, mostrar_precios_oferta } from '../../services/ofertas';
import { print_contrato_pdf } from '../../services/auxiliares';
import { send_document_signaturit } from '../../services/signaturit';
import OfferProgressTimeline from '../ofertas/OfferProgressTimeline';
import OfferWizard from '../ofertas/OfferWizard';
import { OfferWizardProvider } from './OfferWizardContext'; // Import Provider
import { includesBvLabel } from '../../utils/bateriaVirtual';

import OfferHeader from '../ofertas/OfferHeader';
import GestionArchivosOferta from '../modals/GestionArchivosOferta';
import NotasOferta from '../modals/NotasOferta';
import SendSignaturitModal from '../modals/SendSignaturitModal';
import RelanzarOfertaModal from '../modals/RelanzarOfertaModal';
import ConfirmRelanzarModal from '../modals/ConfirmRelanzarModal';
import CancelOfertaModal from "../../components/modals/CancelOfertaModal";

import { determineContentTypeAndExtension } from '../../utils/ofertasUtils';
import { getOfferSignaturitEmail } from '../../utils/signaturit';
import { connect } from 'react-redux';
import './OfferWizardWrapper.css';

const EDIT_LOCK_STAGE_IDS = ['tarifa', 'cliente', 'pagador', 'cups', 'bateria-virtual', 'documentacion'];
const EDIT_LOCK_STAGE_ROUTES = ['tarifas', 'partner', 'pagador', 'cups', 'bateria-virtual', 'documentacion'];

const normalizeStageState = (value) =>
    String(value || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');

const isOfferEditLockedByState = (stateName) => {
    const normalized = normalizeStageState(stateName);
    if (!normalized) return false;

    return [
        'aceptada',
        'oferta_aceptada',
        'firmado',
        'firmada',
        'signed',
        'won',
        'ganado',
        'pendiente_solicitud_atr',
        'rechazada_sol_atr',
        'solicitud_atr',
        'aceptada_sol_atr',
        'aceptada_solicitud_atr',
        'contrato_activado',
        'oferta_no_aceptada',
        'oferta_cancelada',
    ].includes(normalized);
};

const extractSubtypeText = (value) => {
    if (Array.isArray(value)) {
        if (value.length > 1) return String(value[1] || value[0] || '');
        return String(value[0] || '');
    }
    if (value && typeof value === 'object') {
        return String(value.name || value.display_name || value.label || value.value || value.id || '');
    }
    return String(value || '');
};

const normalizeSubtype = (value) =>
    extractSubtypeText(value)
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');

const RELANZAR_SUBTYPE_MATRIX_ELECTRICO = {
    alta_nuevo: [
        'alta_nuevo',
        'cambio_comercializador',
        'cambio_comerc_titular',
        'cambio_comercializador_potencia',
        'cambio_comerc_titular_potencia',
        'cambio_comercializador_autoconsumo',
        'cambio_comerc_titular_autoconsumo',
        'cambio_comerc_potencia_autoconsumo',
        'cambio_completo',
    ],
    cambio_comercializador: ['alta_nuevo', 'cambio_comercializador', 'cambio_comerc_titular'],
    cambio_comerc_titular: ['alta_nuevo', 'cambio_comercializador', 'cambio_comerc_titular'],
    cambio_comercializador_potencia: ['alta_nuevo', 'cambio_comercializador_potencia', 'cambio_comerc_titular_potencia'],
    cambio_comercializador_autoconsumo: ['alta_nuevo', 'cambio_comercializador_autoconsumo', 'cambio_comerc_titular_autoconsumo'],
    cambio_comerc_potencia_autoconsumo: ['alta_nuevo', 'cambio_comerc_potencia_autoconsumo', 'cambio_completo'],
    cambio_comerc_titular_potencia: ['alta_nuevo', 'cambio_comercializador_potencia', 'cambio_comerc_titular_potencia'],
    cambio_comerc_titular_autoconsumo: ['alta_nuevo', 'cambio_comercializador_autoconsumo', 'cambio_comerc_titular_autoconsumo'],
    cambio_completo: ['alta_nuevo', 'cambio_comerc_potencia_autoconsumo', 'cambio_completo'],
};

const RELANZAR_SUBTYPE_GAS_OPTIONS = ['alta_nuevo', 'cambio_comercializador', 'cambio_comerc_titular'];
const RELANZAR_SUBTYPE_GAS_SUPPORTED = new Set(RELANZAR_SUBTYPE_GAS_OPTIONS);

const getAllowedRelaunchSubtypes = (currentSubtype, sectorType) => {
    if (String(sectorType || '').toLowerCase() === 'gas') {
        return RELANZAR_SUBTYPE_GAS_SUPPORTED.has(currentSubtype) ? RELANZAR_SUBTYPE_GAS_OPTIONS : [];
    }
    return RELANZAR_SUBTYPE_MATRIX_ELECTRICO[currentSubtype] || [];
};

const canonicalSubtype = (value) => {
    let normalized = normalizeSubtype(value);
    if (normalized.endsWith('_gas')) {
        normalized = normalized.slice(0, -4);
    }
    const aliases = {
        alta_nueva: 'alta_nuevo',
        cambio_de_comercializador: 'cambio_comercializador',
        cambio_de_comercializador_y_titular: 'cambio_comerc_titular',
        cambio_de_comercializador_y_potencia: 'cambio_comercializador_potencia',
        cambio_comerc_potencia: 'cambio_comercializador_potencia',
        cambio_de_comercializador_y_autoconsumo: 'cambio_comercializador_autoconsumo',
        cambio_comerc_autoconsumo: 'cambio_comercializador_autoconsumo',
        cambio_de_comercializador_y_potencia_y_autoconsumo: 'cambio_comerc_potencia_autoconsumo',
        cambio_comercializador_potencia_autoconsumo: 'cambio_comerc_potencia_autoconsumo',
        cambio_de_comercializador_y_titular_y_potencia: 'cambio_comerc_titular_potencia',
        cambio_de_comercializador_y_titular_y_autoconsumo: 'cambio_comerc_titular_autoconsumo',
        cambio_de_comercializador_y_titular_y_potencia_y_autoconsumo: 'cambio_completo',
    };
    return aliases[normalized] || normalized;
};

const OfferWizardWrapperContent = ({ children, currentStep, user }) => {
    const { oferta_id, sector_type } = useParams();
    const normalizedOfertaId = oferta_id && oferta_id !== "undefined" && oferta_id !== "null"
        ? oferta_id
        : null;
    const navigate = useNavigate();
    const [oferta, setOferta] = useState(null);
    /*
    Camps: autoconsumo, bateria_virtual, crm_polissa, id, llista_preu_global, progress_data, sector_type, state, sub_type_offer, tipo_oferta, type, type_offer
    */
    const [loading, setLoading] = useState(!!normalizedOfertaId);
    const [fetchAttempts, setFetchAttempts] = useState(0);
    const [fetchFailed, setFetchFailed] = useState(false);

    // State for actions
    const [showGestionArchivos, setShowGestionArchivos] = useState(false);
    const [ofertaActiva, setOfertaActiva] = useState(null);
    const [showNotas, setShowNotas] = useState(false);
    const [ofertaNotas, setOfertaNotas] = useState(null);
    const [showSignaturit, setShowSignaturit] = useState(false);
    const [ofertaSignaturit, setOfertaSignaturit] = useState(null);
    const [showRelanzarModal, setShowRelanzarModal] = useState(false);
    const [ofertaToRelaunch, setOfertaToRelaunch] = useState(null);
    const [showConfirmRelanzar, setShowConfirmRelanzar] = useState(false);

    // Action Handlers
    const aceptarOferta = async (id, sector_type) => {
        if (!user) return;
        try {
            await aceptar_oferta(id, user.id, sector_type);
            refreshOferta();
        } catch (error) {
            console.error("Error accepting offer:", error);
        }
    };

    const contratoPdf = async (user_id, id, polissa_id) => {
        try {
            const preciosResult = await mostrar_precios_oferta(id);
            if (!preciosResult) return;

            const archivo = await print_contrato_pdf(user_id, id, polissa_id);
            if (!archivo || typeof archivo !== 'object') return;

            Object.keys(archivo).forEach((arch) => {
                const { datas: base64Data, name: originalName } = archivo[arch];
                const { contentType, extension } = determineContentTypeAndExtension(base64Data);

                const byteCharacters = atob(base64Data);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);

                const blob = new Blob([byteArray], { type: contentType });
                const link = document.createElement("a");
                link.href = URL.createObjectURL(blob);

                link.download = originalName || `archivo.${extension}`;
                link.click();
                URL.revokeObjectURL(link.href);
            });
        } catch (error) {
            console.error("Error downloading contract:", error);
        }
    };

    const openGestionArchivos = (id, name) => {
        setOfertaActiva({ id, name });
        setShowGestionArchivos(true);
    };

    const openNotas = (id, name) => {
        setOfertaNotas({ id, name });
        setShowNotas(true);
    };

    const openSignaturit = (oferta) => {
        setOfertaSignaturit(oferta);
        setShowSignaturit(true);
    };

    const onConfirmSignaturit = async (email, signatureType) => {
        if (!ofertaSignaturit?.id) return;
        try {
            await send_document_signaturit(ofertaSignaturit.id, signatureType, email);
        } catch (err) {
            console.error("Error calling signaturit:", err);
        } finally {
            refreshOferta();
        }
    };

    const handleAutoValidate = async (id) => {
        const updatedOferta = await sync_renovacion_progress(id);
        if (updatedOferta) {
            setOferta(updatedOferta);
        }
    };

    const handleRelanzarOferta = (currentOferta) => {
        const current = canonicalSubtype(currentOferta?.sub_type_offer);
        const allowed = getAllowedRelaunchSubtypes(current, currentOferta?.sector_type);
        if (!allowed.length) {
            return;
        }
        setOfertaToRelaunch(currentOferta);
        setShowConfirmRelanzar(true);
    };

    const handleConfirmRelanzarChangar = () => {
        setShowConfirmRelanzar(false);
        setShowRelanzarModal(true);
    };

    const handleConfirmRelanzarMantener = async () => {
        if (!ofertaToRelaunch) return;
        try {
            const currentSubtype = canonicalSubtype(ofertaToRelaunch?.sub_type_offer);
            const updatedOferta = await relanzar_oferta(ofertaToRelaunch.id, currentSubtype);
            if (updatedOferta) {
                setOferta(updatedOferta);
            }
        } finally {
            setOfertaToRelaunch(null);
        }
    };

    const handleConfirmRelaunch = async (selectedSubtype) => {
        if (!ofertaToRelaunch) return;
        try {
            const updatedOferta = await relanzar_oferta(ofertaToRelaunch.id, selectedSubtype);
            if (updatedOferta) {
                setOferta(updatedOferta);
            }
        } finally {
            setOfertaToRelaunch(null);
        }
    };

//CANCELAR OFERTA
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [selectedOferta, setSelectedOferta] = useState(null);
    // Handler to open the cancel modal
    const handleCancelarClick = (oferta) => {
        setSelectedOferta(oferta);
        setShowCancelModal(true);
    };
    // Handler to confirm cancellation
    const handleCancelarOferta = async (oferta) => {
        const updatedOferta = await cancelar_oferta(oferta.id);
        if (updatedOferta) {
            setOferta(updatedOferta);
        }
    };

    useEffect(() => {
        const fetchOferta = async () => {
            setLoading(true);
            setFetchFailed(false);
            try {
                const data = await get_datos_oferta_erp(normalizedOfertaId);
                if (data) {
                    setOferta(data);
                    setLoading(false);
                    return;
                }
                if (fetchAttempts < 2) {
                    setTimeout(() => {
                        setFetchAttempts((prev) => prev + 1);
                    }, 800);
                    return;
                }
                setOferta(null);
                setFetchFailed(true);
            } catch (error) {
                console.error("Error loading offer:", error);
                setFetchFailed(true);
            } finally {
                setLoading(false);
            }
        };

        if (normalizedOfertaId) {
            fetchOferta();
            return;
        }
        setLoading(false);
        setFetchFailed(false);
        setFetchAttempts(0);
    }, [normalizedOfertaId, fetchAttempts]);

    const refreshOferta = async () => {
        if (!normalizedOfertaId) {
            return;
        }
        try {
            const data = await get_datos_oferta_erp(normalizedOfertaId);
            setOferta(data);
        } catch (error) {
            console.error("Error refreshing offer:", error);
        }
    };

    const handleTimelineNavigation = (route) => {
        if (route && oferta) {
            if (isOfferEditLockedByState(oferta?.state)) {
                return;
            }

            if (blockedStageRoutes.includes(route)) {
                return;
            }

            if (route === 'resumen') {
                navigate(`/contratos/ofertas/${oferta.id}`);
            } else {
                navigate(`/${oferta.sector_type}/${route}/${oferta.id}`);
            }
        }
    };

    //TARIFA COMERCIAL
    const llistaPreuGlobal =
        oferta?.llista_preu_global ||
        oferta?.crm_polissa?.llista_preu_global ||
        oferta?.crm_polissa?.llista_preu;

    /*Tiene autoconsumo si BV en tarifa comercial y autoconsumo con compensacion
    { id: "41", name: "Con excedentes y compensación Individual – Consumo" },
    { id: "42", name: "Con excedentes y compensación Colectivo – Consumo" },
    { id: "43", name: "Con excedentes y compensación Colectivo a través de red – Consumo" }    
    */
    const autoconsumoValue = oferta?.autoconsumo;
    const hasAutoconsumo = autoconsumoValue === "41" || autoconsumoValue === "42" || autoconsumoValue === "43";
    const showVirtualBattery = (hasAutoconsumo && includesBvLabel(llistaPreuGlobal)) && !!oferta?.bateria_virtual;
    // const hasAutoconsumo =
    //     autoconsumoValue !== null &&
    //     autoconsumoValue !== undefined &&
    //     String(autoconsumoValue).trim() !== "" &&
    //     String(autoconsumoValue) !== "00";
    //const showVirtualBattery = (hasAutoconsumo && includesBvLabel(llistaPreuGlobal)) || !!oferta?.bateria_virtual;
    
    const normalizedSubType = normalizeSubtype(oferta?.sub_type_offer);
    const normalizedType = normalizeSubtype(oferta?.type || oferta?.type_offer || oferta?.tipo_oferta);
    const isRenewal =
        normalizedSubType.startsWith('renovacion')
        || normalizedSubType.includes('renovacion')
        || normalizedType.startsWith('renovacion');
    const isStateLocked = isOfferEditLockedByState(oferta?.state);
    const blockedStageIds = isStateLocked ? EDIT_LOCK_STAGE_IDS : (isRenewal ? ['cliente', 'pagador'] : []);
    const blockedStageRoutes = isStateLocked ? EDIT_LOCK_STAGE_ROUTES : (isRenewal ? ['partner', 'pagador'] : []);

    useEffect(() => {
        if (!oferta?.id || !isStateLocked) {
            return;
        }

        navigate(`/contratos/ofertas/${oferta.id}`, {
            replace: true,
            state: {
                editBlocked: true,
                blockedState: oferta.state,
            },
        });
    }, [isStateLocked, oferta?.id, oferta?.state, navigate]);

    if (loading) {
        return (
            <Container className="py-5">
                <div className="text-center">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-3">Cargando oferta...</p>
                </div>
            </Container>
        );
    }

    if (normalizedOfertaId && fetchFailed && !oferta) {
        return (
            <Container className="py-5">
                <div className="text-center">
                    <h3>No se encontró la oferta</h3>
                </div>
            </Container>
        );
    }

    if (isStateLocked) {
        return (
            <Container className="py-5">
                <div className="text-center">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-3">Esta oferta ya no se puede editar por su estado actual.</p>
                </div>
            </Container>
        );
    }

    return (
        <div className="offer-wizard-wrapper bg-light min-vh-100 py-4">
            <Container>
                {/* Offer Header with Actions */}
                {oferta && (
                    <OfferHeader
                        oferta={oferta}
                        user={user}
                        onNavigateBack={() => navigate("/contratos/ofertas")}
                        onAceptar={aceptarOferta}
                        onImprimirContrato={(user_id, id, polissa_id) => contratoPdf(user_id, id, polissa_id)}
                        onGestionArchivos={openGestionArchivos}
                        onVerNotas={openNotas}
                        onEnviarSignaturit={openSignaturit}
                        onSyncRenovacion={handleAutoValidate}
                        onRelanzar={handleRelanzarOferta}
                        onCancelar={handleCancelarClick}
                    />
                )}

                {/* Timeline de Progreso */}
                {oferta?.progress_data && (
                    <Card className="timeline-card mb-4">
                        <Card.Body className="p-3">
                            <OfferProgressTimeline
                                progressData={oferta.progress_data}
                                ofertaId={oferta.id}
                                sectorType={oferta.sector_type}
                                horizontal={true}
                                currentStep={currentStep}
                                onNavigate={handleTimelineNavigation}
                                blockedStages={blockedStageIds}
                                //Reutilitza condició visibilitat de la BV que s’utilitza en la navegació.
                                showVirtualBattery={showVirtualBattery}
                            />
                        </Card.Body>
                    </Card>
                )}

                {/* Contenido de la pantalla actual */}
                <div className="wizard-content-wrapper">
                    {children}
                </div>

                {/* Navegación del Wizard */}
                <OfferWizard
                    currentStep={currentStep}
                    ofertaId={normalizedOfertaId}
                    sectorType={oferta?.sector_type || sector_type}
                    onSave={refreshOferta}
                    showVirtualBattery={showVirtualBattery}
                    blockedStages={blockedStageIds}
                />
                {/* Modals for actions */}
                <GestionArchivosOferta
                    show={showGestionArchivos}
                    handleClose={() => setShowGestionArchivos(false)}
                    oferta={ofertaActiva}
                    user={user}
                />

                <NotasOferta
                    show={showNotas}
                    handleClose={() => setShowNotas(false)}
                    oferta={ofertaNotas}
                    user={user}
                />

                <SendSignaturitModal
                    show={showSignaturit}
                    onHide={() => setShowSignaturit(false)}
                    onClose={refreshOferta}
                    defaultEmail={getOfferSignaturitEmail(ofertaSignaturit)}
                    onConfirm={onConfirmSignaturit}
                />

                <ConfirmRelanzarModal
                    show={showConfirmRelanzar}
                    onHide={() => setShowConfirmRelanzar(false)}
                    currentSubtype={ofertaToRelaunch ? canonicalSubtype(ofertaToRelaunch?.sub_type_offer) : ""}
                    onConfirmMantener={handleConfirmRelanzarMantener}
                    onConfirmChangar={handleConfirmRelanzarChangar}
                />

                <RelanzarOfertaModal
                    show={showRelanzarModal}
                    onHide={() => setShowRelanzarModal(false)}
                    allowedSubtypes={ofertaToRelaunch ? getAllowedRelaunchSubtypes(canonicalSubtype(ofertaToRelaunch?.sub_type_offer), ofertaToRelaunch?.sector_type) : []}
                    onConfirm={handleConfirmRelaunch}
                />

                <CancelOfertaModal
                    show={showCancelModal}
                    onHide={() => setShowCancelModal(false)}
                    onConfirm={handleCancelarOferta}
                    oferta={selectedOferta}
                />
            </Container>
        </div>
    );
};

const mapStateToProps = state => ({
    user: state.Auth ? state.Auth.user : null
});

const OfferWizardWrapperContentConnected = connect(mapStateToProps)(OfferWizardWrapperContent);

const OfferWizardWrapper = (props) => (
    <OfferWizardProvider>
        <OfferWizardWrapperContentConnected {...props} />
    </OfferWizardProvider>
);

export default OfferWizardWrapper;
